import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { UserSelectors } from '@core/account-data';
import { CandidateSelectors } from '@core/candidate-data';
import type { ShareOnTheGoRequestPayload } from '@core/candidate-types';
import {
  DeleteEventPayload,
  InterviewEvent,
  InterviewStatus,
  ScheduleEventSet,
  ScheduleSuccessData,
} from '@core/interview-events-types';
import { InterviewScheduleResultRequestsCollectionSelectors } from '@core/interview-schedules-data';
import {
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import { JobsWrapperDataService, StageSelectors } from '@core/offers-data';
import { TalentPoolsBriefSelectors } from '@core/talent-pools-data';

import type { InterviewEventParams } from '../types';
import type {
  InterviewEventBackendResponseMany,
  InterviewEventBackendResponseOne,
} from './interview-event.http-client';

type InterviewEventsMeta = { scopeMeta: { counters: { pastDue: number; upcoming: number } } };

const {
  actions,
  selectors: defaultSelectors,
  reducer,
  namespace,
} = createEntityCollectionStore<InterviewEvent, InterviewEventsMeta>()('InterviewEvents', {
  references: makeRef => [
    makeRef('adminIds', 'admins', UserSelectors.byIds),
    makeRef('candidateId', 'candidate', candidateId => CandidateSelectors.byId(candidateId!)!),
    makeRef('offerId', 'offer', offerId => JobsWrapperDataService.jobItemByIdSelector(offerId!)!),
    makeRef('talentPoolId', 'talentPool', talentPoolId =>
      TalentPoolsBriefSelectors.byId(talentPoolId),
    ),
    makeRef('stageId', 'stage', StageSelectors.byId),
    makeRef(
      'interviewResultRequestsIds',
      'interviewResultRequests',
      InterviewScheduleResultRequestsCollectionSelectors.byIds,
    ),
    makeRef(
      'interviewDeferredResultRequestsIds',
      'interviewDeferredResultRequests',
      InterviewScheduleResultRequestsCollectionSelectors.byIds,
    ),
  ],
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const selectId = ({ id }: InterviewEvent) => id;
    const defaultActions = getEntityCollectionActions(adapter, selectId);

    return {
      fetchCollection: (
        state,
        payload: { params: InterviewEventParams; scope: string; replace?: boolean },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.resetAll(next) : next),
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchCollectionSuccess: (
        state,
        payload: {
          data: InterviewEventBackendResponseMany;
          scope: string;
          params: InterviewEventParams;
          replace?: boolean;
        },
      ) =>
        pipeState(
          state,
          next =>
            adapter.setScopeMetaCustom(
              next,
              { counters: payload.data.meta?.counters },
              { scope: payload.scope },
            ),
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next => adapter.addMany(next, payload.data.interviewEvents, { scope: payload.scope }),
          next =>
            // TODO to be changed to backend meta response, after backend is ready
            adapter.setPaginationMeta(
              next,
              {
                limit: <number>payload.params.limit,
                page: <number>payload.params.page,
                totalCount: null,
              },
              { scope: payload.scope },
            ),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.interviewEvents.map(e => e.id),
            }),
        ),
      fetchCollectionFailed: (state, payload) =>
        defaultActions.fetchCollectionFailed(state, payload),

      fetchItem: (state, payload: { id: number; scope?: string }) => adapter.markAsPending(state),
      fetchItemSuccess: (
        state,
        payload: { data: InterviewEventBackendResponseOne; scope?: string },
      ) =>
        pipeState(
          state,
          next =>
            adapter.addOne(next, payload.data.interviewEvent, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
            }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [payload.data.interviewEvent.id],
            }),
        ),

      create: (
        state,
        payload: { data: { value: Partial<InterviewEvent>; candidateId?: number }; scope?: string },
      ) => adapter.markAsPending(state),
      createSuccess: (state, payload: { data: InterviewEvent; scope?: string }) =>
        pipeState(
          state,
          next => adapter.addOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),

      update: (
        state,
        payload: {
          data: {
            id: number;
            value: {
              interviewEvent: Partial<InterviewEvent>;
              shareOnTheGoPayload?: ShareOnTheGoRequestPayload;
            };
          };
          scope?: string;
        },
      ) => adapter.markAsPending(state),
      updateSuccess: (state, payload: { data: InterviewEvent; scope?: string }) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),

      schedule: ngrxEvent<{ id: number; scheduleEventSet: ScheduleEventSet }>(),
      scheduleSuccess: ngrxEvent<ScheduleSuccessData>(),
      scheduleFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

      delete: ngrxEvent<{ id: number; body: DeleteEventPayload }>(),
      deleteSuccess: (state, payload: { data: InterviewEventBackendResponseOne }) =>
        adapter.removeOne(state, payload.data.interviewEvent.id),
      deleteFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    };
  },
});

const selectors = {
  ...defaultSelectors,
  getEntitiesWithReferencesAndPagination: memoizeSelector((scope: string) =>
    createSelector(
      defaultSelectors.pagination({ scope }),
      defaultSelectors.entitiesWithReferences({ scope }),
      (pagination, entities) => ({
        meta: pagination,
        rows: entities,
      }),
    ),
  ),
};

export const interviewEventsDashboardScope = (status: string, scope: string) =>
  `dashboard_${scope}_${status}`;

export const interviewEventsCandidateScope = (candidateId: number) => `candidate_${candidateId}`;

export const interviewEventsStatusCandidateScope = (candidateId: number, status: InterviewStatus) =>
  `candidate_${status}_${candidateId}`;

export {
  actions as InterviewEventsActions,
  selectors as InterviewEventsSelectors,
  reducer as InterviewEventsReducer,
  namespace as InterviewEventsNamespace,
};
